import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter, Routes, Route, Navigate, useParams } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'sonner';
import App from './App';
import LoginPage from './pages/Login';
import MyTicketsPage from './pages/MyTickets';
import DispatchPage from './pages/Dispatch';
import TicketDetailPage from './pages/TicketDetail';
import OpportunitiesPage from './pages/Opportunities';
import OpportunityDetailPage from './pages/OpportunityDetail';
import AccountPage from './pages/Account';
import AccountInfoPage from './pages/AccountInfo';
import AccountNoteTemplatesPage from './pages/AccountNoteTemplates';
import AccountBookmarksPage from './pages/AccountBookmarks';
import AdminPage from './pages/Admin';
import AdminUsersPage from './pages/AdminUsers';
import AdminWebhookPage from './pages/AdminWebhook';
import AdminAiAssistantPage from './pages/AdminAiAssistant';
import AdminProjectStatusPage from './pages/AdminProjectStatus';
import AdminSystemPage from './pages/AdminSystem';
import AdminIntegrationsPage from './pages/AdminIntegrations';
import AdminMappingsPage from './pages/AdminMappings';
import AdminCippPage from './pages/AdminCipp';
import CippTenantsPage from './pages/cipp/Tenants';
import CippTenantDetailPage from './pages/cipp/TenantDetail';
import CippDevicesPage from './pages/cipp/Devices';
import CippSecurityDashboardPage from './pages/cipp/SecurityDashboard';
import HomePage from './pages/Home';
import DomainsPage from './pages/domains/Domains';
import DomainDetailPage from './pages/domains/DomainDetail';
import DocsOrganizationsPage from './pages/docs/Organizations';
import DocsOrganizationPage from './pages/docs/Organization';
import DocsConfigurationsPage from './pages/docs/Configurations';
import DocsExpirationsPage from './pages/docs/Expirations';
import NetworkHomePage from './pages/network/NetworkHome';
import { SophosAlertsPage, SophosDashboardPage, SophosFirewallsPage, SophosSubscriptionsPage, SophosTenantsPage } from './pages/network/Sophos';
import { UnifiConsolesPage, UnifiDashboardPage, UnifiDevicesPage, UnifiSitesPage, UnifiWanPage } from './pages/network/Unifi';
import AdminNetworkMappingPage from './pages/AdminNetworkMapping';
import InsightsCoveragePage from './pages/insights/Coverage';
import InsightsGapsPage from './pages/insights/Gaps';
import FindPage from './pages/insights/Find';
import SocOverviewPage from './pages/soc/SocOverview';
import SocIncidentsPage from './pages/soc/SocIncidents';
import SocIncidentPage from './pages/soc/SocIncident';
import SocAgentsPage from './pages/soc/SocAgents';
import SocIdentitiesPage from './pages/soc/SocIdentities';
import SocOrganizationsPage from './pages/soc/SocOrganizations';
import AiChatPage from './pages/ai/AiChat';
import { FEATURES } from './lib/features';
import DashboardsPage from './pages/dashboards/Dashboards';
import DashboardPage from './pages/dashboards/Dashboard';
import DashboardDisplayPage from './pages/dashboards/DashboardDisplay';
import DeviceOrbitPage from './pages/insights/DeviceOrbit';
import PersonOrbitPage from './pages/insights/PersonOrbit';
import CompaniesPage from './pages/companies/Companies';
import OrbitPage from './pages/companies/Orbit';
import AdminModuleSettingsPage from './pages/AdminModuleSettings';
import BackupSaasCustomersPage from './pages/backup/SaasCustomers';
import BackupSaasCustomerPage from './pages/backup/SaasCustomer';
import CspDashboardPage from './pages/csp/CspDashboard';
import CspCustomersPage from './pages/csp/CspCustomers';
import CspLicencesPage from './pages/csp/CspLicences';
import CspProductsPage from './pages/csp/CspProducts';
import CspRequestsPage from './pages/csp/CspRequests';
import CspUploadPage from './pages/csp/CspUpload';
import CspMappingsPage from './pages/csp/CspMappings';
import CreateTicketPage from './pages/CreateTicket';
import TicketSearchPage from './pages/TicketSearch';
import CreateOpportunityPage from './pages/CreateOpportunity';
import OpportunitySearchPage from './pages/OpportunitySearch';
import SearchPage from './pages/SearchPage';
import RmmDevicesPage from './pages/rmm/Devices';
import RmmDeviceDetailPage from './pages/rmm/DeviceDetail';
import RmmAlertsPage from './pages/rmm/Alerts';
import RmmSitesPage from './pages/rmm/Sites';
import { AuthGuard } from './components/AuthGuard';
import { ROUTES, LEGACY_REDIRECTS, LEGACY_RECORD_REDIRECTS } from './lib/routes';
import { Loader2 } from 'lucide-react';
import './index.css';

/**
 * Forwards a pre-`/psa` record URL (e.g. `/tickets/123`) to its new home,
 * carrying the id. Links copied out of an AI-assistant chat still land.
 */
function LegacyRecordRedirect({ build }: { build: (id: string) => string }) {
  const { id } = useParams();
  return <Navigate to={build(id ?? '')} replace />;
}

// Dashboards are desktop-only and pull in recharts — lazy-load so that chunk
// stays out of the main/mobile bundle.
const ServiceDeskDashboardPage = React.lazy(() => import('./pages/ServiceDeskDashboard'));
const SalesDashboardPage = React.lazy(() => import('./pages/SalesDashboard'));
// Projects are code-split: the main bundle must stay under the PWA's 2 MiB precache limit.
const ProjectsListPage = React.lazy(() => import('./pages/projects/ProjectsList'));
const ProjectDetailPage = React.lazy(() => import('./pages/projects/ProjectDetail'));
const MyProjectTasksPage = React.lazy(() => import('./pages/projects/MyTasks'));
const RmmOverviewDashboardPage = React.lazy(() => import('./pages/rmm/OverviewDashboard'));
const RmmPatchingDashboardPage = React.lazy(() => import('./pages/rmm/PatchingDashboard'));
const RmmRemoteAccessPage = React.lazy(() => import('./pages/rmm/RemoteAccess'));

function LazyDashboard({ children }: { children: React.ReactNode }) {
  return (
    <React.Suspense
      fallback={
        <div className="flex items-center justify-center gap-2 py-20 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading…
        </div>
      }
    >
      {children}
    </React.Suspense>
  );
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      refetchOnWindowFocus: true,
      retry: 1,
    },
  },
});

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <Routes>
          {/* Public route — accessible without a session */}
          <Route path="/login" element={<LoginPage />} />

          {/* Wall display: signed in, but outside the app shell (no sidebar / header). */}
          <Route path="/dashboards/:id/display" element={<AuthGuard><DashboardDisplayPage /></AuthGuard>} />

          {/* All app routes require authentication */}
          <Route path="/" element={<AuthGuard><App /></AuthGuard>}>
            <Route index element={<HomePage />} />

            {/* ── PSA module (Autotask) ─────────────────────────────── */}
            <Route path="psa">
              <Route index element={<Navigate to={ROUTES.psa.tickets.mine} replace />} />

              <Route path="dashboards"              element={<Navigate to={ROUTES.psa.dashboards.serviceDesk} replace />} />
              <Route path="dashboards/service-desk" element={<LazyDashboard><ServiceDeskDashboardPage /></LazyDashboard>} />
              <Route path="dashboards/sales"        element={<LazyDashboard><SalesDashboardPage /></LazyDashboard>} />

              {/* Static segments outrank `:id` in React Router's ranking, so
                  `tickets/mine` and `tickets/:id` coexist safely. */}
              <Route path="tickets/mine"   element={<MyTicketsPage />} />
              <Route path="tickets/queues" element={<DispatchPage />} />
              <Route path="tickets/search" element={<TicketSearchPage />} />
              <Route path="tickets/new"    element={<CreateTicketPage />} />
              <Route path="tickets/:id"    element={<TicketDetailPage />} />
              <Route path="tickets"        element={<Navigate to={ROUTES.psa.tickets.mine} replace />} />

              <Route path="opportunities/mine"   element={<OpportunitiesPage assignedTo="me" />} />
              <Route path="opportunities/search" element={<OpportunitySearchPage />} />
              <Route path="opportunities/new"    element={<CreateOpportunityPage />} />
              <Route path="opportunities/:id"    element={<OpportunityDetailPage />} />
              <Route path="opportunities"        element={<OpportunitiesPage assignedTo="any" />} />

              <Route path="search" element={<SearchPage />} />

              <Route path="projects"                   element={<LazyDashboard><ProjectsListPage /></LazyDashboard>} />
              <Route path="projects/mine"              element={<LazyDashboard><ProjectsListPage mine /></LazyDashboard>} />
              <Route path="projects/tasks/mine"        element={<LazyDashboard><MyProjectTasksPage /></LazyDashboard>} />
              <Route path="projects/:id"               element={<LazyDashboard><ProjectDetailPage /></LazyDashboard>} />
              <Route path="projects/:id/tasks/:taskId" element={<LazyDashboard><ProjectDetailPage /></LazyDashboard>} />
            </Route>

            {/* ── RMM module (Datto RMM) ─────────────────────────────── */}
            <Route path="rmm">
              <Route index element={<Navigate to={ROUTES.rmm.dashboards.overview} replace />} />
              <Route path="dashboards" element={<Navigate to={ROUTES.rmm.dashboards.overview} replace />} />
              <Route path="dashboards/overview" element={<LazyDashboard><RmmOverviewDashboardPage /></LazyDashboard>} />
              <Route path="dashboards/patching" element={<LazyDashboard><RmmPatchingDashboardPage /></LazyDashboard>} />
              <Route path="devices"      element={<RmmDevicesPage />} />
              <Route path="devices/:uid" element={<RmmDeviceDetailPage />} />
              <Route path="alerts"       element={<RmmAlertsPage />} />
              <Route path="sites"        element={<RmmSitesPage />} />
              <Route path="sites/:uid"   element={<RmmSitesPage />} />
              <Route path="sites/client/:companyId" element={<RmmSitesPage />} />
              <Route path="remote"       element={<LazyDashboard><RmmRemoteAccessPage /></LazyDashboard>} />
            </Route>

            {/* ── CIPP module (Microsoft 365) ───────────────────────── */}
            <Route path="cipp">
              <Route index element={<Navigate to={ROUTES.cipp.tenants} replace />} />
              <Route path="tenants"             element={<CippTenantsPage />} />
              <Route path="tenants/:customerId" element={<CippTenantDetailPage />} />
              <Route path="devices"             element={<CippDevicesPage />} />
              <Route path="dashboards/security" element={<CippSecurityDashboardPage />} />
            </Route>

            {/* ── CSP module (Microsoft NCE licences) ───────────────── */}
            <Route path="csp">
              <Route index element={<Navigate to={ROUTES.csp.dashboard} replace />} />
              <Route path="dashboard" element={<CspDashboardPage />} />
              <Route path="customers" element={<CspCustomersPage />} />
              <Route path="licences"  element={<CspLicencesPage />} />
              <Route path="products"  element={<CspProductsPage />} />
              <Route path="requests"  element={<CspRequestsPage />} />
              <Route path="upload"    element={<CspUploadPage />} />
              <Route path="mappings"  element={<CspMappingsPage />} />
              {/* CSP settings moved to Admin → CSP (org-wide). */}
              <Route path="settings"  element={<Navigate to={ROUTES.admin.csp} replace />} />
            </Route>

            {/* ── Network (Sophos firewalls + UniFi) ─────────────────── */}
            <Route path="network">
              <Route index element={<NetworkHomePage />} />
              {/* Sophos and UniFi are separate areas with their own data. */}
              <Route path="sophos"               element={<SophosDashboardPage />} />
              <Route path="sophos/firewalls"     element={<SophosFirewallsPage />} />
              <Route path="sophos/subscriptions" element={<SophosSubscriptionsPage />} />
              <Route path="sophos/alerts"        element={<SophosAlertsPage />} />
              <Route path="sophos/tenants"       element={<SophosTenantsPage />} />
              <Route path="unifi"                element={<UnifiDashboardPage />} />
              <Route path="unifi/sites"          element={<UnifiSitesPage />} />
              <Route path="unifi/consoles"       element={<UnifiConsolesPage />} />
              <Route path="unifi/devices"        element={<UnifiDevicesPage />} />
              <Route path="unifi/wan"            element={<UnifiWanPage />} />
              {/* Earlier combined pages (2026-10-13) → their new homes. */}
              <Route path="firewalls" element={<Navigate to={ROUTES.network.sophos.firewalls} replace />} />
              <Route path="devices"   element={<Navigate to={ROUTES.network.unifi.devices} replace />} />
            </Route>

            {/* ── Domains (dmarcian + CIPP DNS) ──────────────────────── */}
            <Route path="domains"         element={<DomainsPage />} />
            <Route path="domains/:domain" element={<DomainDetailPage />} />

            {/* ── SOC (Huntress) — flat pages ─────────────────────────── */}
            <Route path="soc"                element={<SocOverviewPage />} />
            <Route path="soc/incidents"      element={<SocIncidentsPage />} />
            <Route path="soc/incidents/:id"  element={<SocIncidentPage />} />
            <Route path="soc/agents"         element={<SocAgentsPage />} />
            <Route path="soc/identities"     element={<SocIdentitiesPage />} />
            <Route path="soc/organizations"  element={<SocOrganizationsPage />} />

            {/* ── AI: chat with the Foundry agent (Synfo MCP). Parked: lib/features.ts ── */}
            {FEATURES.aiChat && <Route path="ai" element={<AiChatPage />} />}

            {/* ── Custom dashboards (any module's metrics; saved + shared) ── */}
            <Route path="dashboards"     element={<DashboardsPage />} />
            <Route path="dashboards/:id" element={<DashboardPage />} />

            {/* ── Insights (cross-module) + Orbit ──────────────── */}
            <Route path="insights">
              <Route index element={<Navigate to={ROUTES.insights.coverage} replace />} />
              <Route path="coverage" element={<InsightsCoveragePage />} />
              <Route path="gaps" element={<InsightsGapsPage />} />
              <Route path="find" element={<FindPage />} />
              <Route path="devices/:key" element={<DeviceOrbitPage />} />
              <Route path="people/:key" element={<PersonOrbitPage />} />
            </Route>
            <Route path="companies"     element={<CompaniesPage />} />
            <Route path="companies/:id" element={<OrbitPage />} />

            {/* ── Planned modules — placeholder pages ───────────────── */}
            <Route path="docs"                         element={<Navigate to={ROUTES.docs.organizations} replace />} />
            <Route path="docs/organizations"           element={<DocsOrganizationsPage />} />
            <Route path="docs/organizations/:orgId"    element={<DocsOrganizationPage />} />
            <Route path="docs/configurations"          element={<DocsConfigurationsPage />} />
            <Route path="docs/expirations"             element={<DocsExpirationsPage />} />
            {/* ── Backup module (Datto SaaS Protection) ────────────── */}
            <Route path="backup">
              <Route index element={<Navigate to={ROUTES.backup.saas} replace />} />
              <Route path="saas"     element={<BackupSaasCustomersPage />} />
              <Route path="saas/:id" element={<BackupSaasCustomerPage />} />
            </Route>

            {/* ── Pre-/psa paths (incl. /dashboards/*), kept resolving ── */}
            {LEGACY_REDIRECTS.map(({ from, to }) => (
              <Route key={from} path={from} element={<Navigate to={to} replace />} />
            ))}
            {LEGACY_RECORD_REDIRECTS.map(({ from, build }) => (
              <Route key={from} path={from} element={<LegacyRecordRedirect build={build} />} />
            ))}

            <Route path="account" element={<AccountPage />}>
              <Route path="info" element={<AccountInfoPage />} />
              <Route path="note-templates" element={<AccountNoteTemplatesPage />} />
              <Route path="bookmarks" element={<AccountBookmarksPage />} />
            </Route>
            <Route path="admin" element={<AdminPage />}>
              <Route path="users"        element={<AdminUsersPage />} />
              <Route path="webhook"      element={<AdminWebhookPage />} />
              <Route path="ai-assistant" element={<AdminAiAssistantPage />} />
              <Route path="psa/project-status" element={<AdminProjectStatusPage />} />
              <Route path="cipp"         element={<AdminCippPage />} />
              <Route path="cipp/settings" element={<AdminModuleSettingsPage module="cipp" />} />
              <Route path="psa"          element={<AdminModuleSettingsPage module="psa" />} />
              <Route path="rmm"          element={<AdminModuleSettingsPage module="rmm" />} />
              <Route path="csp"          element={<AdminModuleSettingsPage module="csp" />} />
              <Route path="backup"       element={<AdminModuleSettingsPage module="backup" />} />
              <Route path="insights"     element={<AdminModuleSettingsPage module="insights" />} />
              <Route path="soc"          element={<AdminModuleSettingsPage module="soc" />} />
              {FEATURES.aiChat && <Route path="ai" element={<AdminModuleSettingsPage module="ai" />} />}
              <Route path="network"          element={<AdminNetworkMappingPage />} />
              <Route path="network/settings" element={<AdminModuleSettingsPage module="network" />} />
              <Route path="domains"          element={<AdminModuleSettingsPage module="domains" />} />
              <Route path="system"       element={<AdminSystemPage />} />
              <Route path="integrations" element={<AdminIntegrationsPage />} />
              <Route path="mappings"     element={<AdminMappingsPage />} />
            </Route>
          </Route>
        </Routes>
      </BrowserRouter>
      <Toaster richColors position="bottom-right" />
    </QueryClientProvider>
  </React.StrictMode>,
);
